import { _t } from 'components/Utils/translation'
import { scaleBand, scaleLinear } from 'd3'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useResizeObserver, useWindowSize } from 'usehooks-ts'

const svgheight = 300

const GenderSVGColumnChart = ({
  title,
  data,
  domain,
  unit,
  labels,
  user,
  userIsInGroup,
  inverted = false,
}: {
  title: string
  data: { values: number[]; max?: number }
  domain: any[]
  unit?: string
  labels: string[]
  user?: Answer
  userIsInGroup?: boolean
  inverted?: boolean
}) => {
  const usermarker = {
    width: 50,
    height: 30,
    label: _t('user'),
  }
  const ref = useRef<HTMLDivElement>(null)
  const [valueMax, setValuemax] = useState(0)
  const [valueSum, setValueSum] = useState(0)
  const [tooltipData, setTooltipData] = useState<any>()
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 711
  const { width = 0 } = useResizeObserver({
    ref,
    box: 'border-box',
  })
  const [chartWidth, setChartWidth] = useState(width)
  const [hoveredBar, setHoveredBar] = useState<number | undefined>()

  const [drawData, setDrawData] = useState<any>()
  useEffect(() => {
    if (!data) return
    const d = Object.entries(data) as [string, number][]
    const sum = d.map((m) => m[1]).reduce((acc, curr) => acc + +curr, 0)
    var d_rel = d.map((m) => {
      return [m[0], Math.round((m[1] * 100) / sum)]
    })
    const max = Math.max(
      ...d_rel.map((m) => {
        return Math.round(+m[1])
      })
    )
    setValuemax(max)
    setValueSum(sum)
    setDrawData(d_rel)
  }, [data])

  var plotarea = {
    top: usermarker.height + 20,
    bottom: svgheight - 15,
    left: 40,
    right: chartWidth - (windowSize.width < 711 ? 30 : 30),
  }

  useEffect(() => {
    if (ref.current) {
      var w = ref.current.getBoundingClientRect().width
      setChartWidth(w)
    } else {
      setChartWidth(width)
    }
  }, [width, ref, isMobile])

  var x = useMemo(
    () =>
      scaleBand<number>().domain(domain).range([plotarea.left, plotarea.right]),
    [chartWidth, domain, isMobile]
  )
  var y = useMemo(
    () =>
      scaleLinear()
        .domain([0, valueMax])
        .range([plotarea.top, plotarea.bottom]),
    [valueMax, plotarea.top, plotarea.bottom]
  )
  var barwidth = x.bandwidth()

  function toPercent(value: number) {
    return (value * 100) / valueSum
  }

  return (
    <GenderSVGColumnChartContainer
      className='GenderSVGColumnChartContainer'
      ref={ref}
      onMouseLeave={() => {
        setTooltipData(undefined)
        setHoveredBar(undefined)
      }}
    >
      <Title>{title}</Title>
      <SVG height={svgheight + 'px'}>
        <g transform={`translate(0,0)`}>
          {domain.map((d, i) => (
            <rect
              x={x(+d)}
              y={plotarea.top}
              width={Math.floor(barwidth) - 1}
              height={plotarea.bottom - plotarea.top}
              fill={inverted ? 'var(--site-background)' : 'var(--section-bg'}
            />
          ))}
          <AxisLabelY
            x={plotarea.left - 8}
            y={plotarea.top + 10}
            textAnchor='end'
          >
            {valueMax}%
          </AxisLabelY>
          <AxisLabelY
            x={plotarea.left - 8}
            y={plotarea.bottom + 2}
            textAnchor='end'
          >
            {'0'}
          </AxisLabelY>

          {drawData &&
            drawData.map((v: [string, number], j: number) => {

              var barcolor = +v[0] === hoveredBar ? '#894185' : '#A757A3'
              return (
                <>
                  <rect
                    x={x(+v[0])}
                    y={plotarea.top}
                    width={barwidth}
                    height={plotarea.bottom - plotarea.top}
                    fill={'transparent'}
                    onClick={() => {
                      if (!isMobile) return
                      if (v[1] === 0) return
                      var xpos = x(+v[0]) || 0
                      setTooltipData({
                        x: xpos,
                        y: plotarea.top + plotarea.bottom - y(v[1]),
                        v: +v[1].toFixed(1),
                      })
                      setHoveredBar(+v[0])
                    }}
                    onPointerMove={() => {
                      if (v[1] === 0) return
                      var xpos = x(+v[0]) || 0
                      setTooltipData({
                        x: xpos,
                        y: plotarea.top + plotarea.bottom - y(v[1]),
                        v: +v[1].toFixed(1),
                      })
                      setHoveredBar(+v[0])
                    }}
                    onPointerLeave={() => {
                      setTooltipData(undefined)
                      setHoveredBar(undefined)
                    }}
                  />
                  <rect
                    pointerEvents={'none'}
                    x={x(+v[0])}
                    y={plotarea.top + plotarea.bottom - y(v[1])}
                    width={barwidth}
                    height={y(v[1]) - plotarea.top}
                    fill={
                      user === +v[0] && userIsInGroup ? '#00f0ff' : barcolor
                    }
                    stroke='var(--site-background)'
                  />
                </>
              )
            })}

          <g
            transform={`translate(${
              (x(user as number) || 0) - usermarker.width / 2 + barwidth / 2
            },${0})`}
            pointerEvents={'none'}
            style={{ userSelect: 'none' }}
          >
            <rect
              width={usermarker.width}
              height={usermarker.height}
              fill='#00f0ff'
            />
            <polygon
              transform={`translate(${usermarker.width / 2 - 10},${
                usermarker.height
              })`}
              points={`0,0 20,0 10,15`}
              fill='#00f0ff'
            />
            <text
              className='USER'
              fontSize={15}
              y={usermarker.height - 9}
              x={usermarker.width / 2}
              textAnchor='middle'
              style={{ userSelect: 'none' }}
            >
              {_t('user') || 'aaa'}
            </text>
          </g>

          <AxisLabel x={plotarea.left} y={plotarea.bottom + 16}>
            {domain[0]}
            {unit}
          </AxisLabel>
          {domain[domain.length - 1] >= 10 && (
            <AxisLabel
              x={
                (x(domain[Math.round(domain.length / 2) - 1]) || 0) +
                barwidth / 2
              }
              y={plotarea.bottom + 16}
              textAnchor='middle'
            >
              {domain[Math.round(domain.length / 2) - 1]}
              {unit}
            </AxisLabel>
          )}
          <AxisLabel
            x={plotarea.right}
            y={plotarea.bottom + 16}
            textAnchor='end'
          >
            {domain[domain.length - 1]}
            {unit}
          </AxisLabel>
          <ChartLabel y={plotarea.bottom + 40}></ChartLabel>
        </g>
        <OverlayRect
          opacity={tooltipData ? 1 : 0}
          x={-50}
          width={plotarea.right + 100}
          height={plotarea.top + 2}
          y={-7}
          fill={inverted ? 'var(--section-bg)' : 'var(--site-background)'}
        />

        {tooltipData && (
          <g transform={`translate(${tooltipData.x + barwidth / 2},${7})`}>
            <line
              x1={0}
              y1={plotarea.top - 10}
              x2={0}
              y2={20}
              stroke='var(--text-color)'
              pointerEvents={'none'}
            />

            <TTText
              x={tooltipData.x > chartWidth - 100 ? -4 : 4}
              y={14}
              textAnchor={'middle'}
              fill='#202346'
            >
              {tooltipData.v.toLocaleString() + '%'}
            </TTText>
          </g>
        )}
      </SVG>
      <AxisKey
        style={{
          paddingLeft: plotarea.left,
          width: `calc(100% - ${plotarea.left + (isMobile ? 10 : 30)}px)`,
        }}
      >
        <div>{labels[0]}</div>
        <div style={{ textAlign: 'right' }}>{labels[1]}</div>
      </AxisKey>
    </GenderSVGColumnChartContainer>
  )
}

const AxisKey = styled.div`
  line-height: 1em;
  font-size: 14px;
  display: flex;
  justify-content: space-between;

  div {
    max-width: 38%;
  }
`
const OverlayRect = styled.rect`
  transition: all 0.2s ease-in-out;
`

const TTText = styled.text`
  font-size: 14px;
  font-weight: 700;
  fill: var(--text-color);
`
const ChartLabel = styled.text`
  font-size: 18px;
  font-weight: 700;
`

const AxisLabel = styled.text`
  font-size: 18px;
  font-size: 14px;
  opacity: 1;
  fill: var(--axis-labels);
`
const AxisLabelY = styled.text`
  font-size: 14px;
  font-size: 12px;
  opacity: 0.5;
  fill: var(--axis-labels);
`
const GenderSVGColumnChartContainer = styled.div`
  z-index: 30;
  margin-top: 60px;
  max-width: 668px;
  margin-left: auto;
  margin-right: auto;

  @media screen and (max-width: 711px) {
    max-width: calc(100vw - 40px);
    margin-left: unset;
    margin-right: unset;
    margin-top: 0px;
    padding: 20px;
    padding-right: 0px;
  }
`

const SVG = styled.svg`
  width: 100%;
  height: ${svgheight}px;
  overflow: visible;
  text {
    user-select: none;
  }
  /* margin-top: 40px; */
`
const Title = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 1.4em;
  margin-bottom: 30px;
  /* border:1px solid #f00; */
`
export default GenderSVGColumnChart
